import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useBrandsProductsNavigation } from '../hooks/use-brands-products-navigation';
import { useBrandsWithThemes } from '../hooks/use-brands-with-themes';
import { useProductsWithThemes } from '../hooks/use-products-with-themes';
import { useThemesByContext } from '../hooks/use-themes-by-context';
import type { DrillDownContext, VolumeType } from '../types';
import { BrandProductTable } from './brand-product.table';
import { ThemeTable } from './theme.table';

import { Button } from '@/shared/components/shadcn/button';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/shadcn/tabs';
import { useSelectedSavedQuery } from '@/shared/hooks/use-selected-saved-query';
import { useSharedDateRange } from '@/shared/hooks/use-shared-date-range';
import { usePublicFilter, type PublicFilter } from '../../shared/public-filter/use-public-filter';

export function BrandsProductsSection() {
  const { query: selectedQuery } = useSelectedSavedQuery();
  const { dateRange } = useSharedDateRange();
  const { publicFilter } = usePublicFilter(selectedQuery.id);
  const {
    viewType,
    volumeType,
    drillDownContext,
    isDrillDown,
    setViewType,
    setVolumeType,
    drillDown,
    goBack,
  } = useBrandsProductsNavigation();

  return (
    <div className="p-6">
      <div className="rounded-lg border bg-white shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-between p-6">
          <div className="flex items-center gap-2">
            {isDrillDown ? (
              // Drill-down header with back button
              <>
                <Button variant="outline" size="icon" onClick={goBack} className="h-10 w-10">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-card-foreground text-xl font-semibold">
                  {drillDownContext?.name}
                </span>
                <ChevronRight className="text-muted-foreground h-4 w-4" />
                <span className="text-card-foreground text-xl font-semibold">
                  {drillDownContext?.sentiment === 'positive' ? 'Positive' : 'Negative'} Themes
                </span>
              </>
            ) : (
              // Root level tabs
              <Tabs
                value={viewType}
                onValueChange={(value) => setViewType(value as 'brands' | 'products')}
              >
                <TabsList>
                  <TabsTrigger value="brands">🏷️ Brands</TabsTrigger>
                  <TabsTrigger value="products">📦 Products</TabsTrigger>
                </TabsList>
              </Tabs>
            )}
          </div>

          {/* Volume toggle */}
          <Tabs
            value={volumeType}
            onValueChange={(value) => setVolumeType(value as 'mention' | 'post')}
          >
            <TabsList>
              <TabsTrigger value="mention">Mention</TabsTrigger>
              <TabsTrigger value="post">Post volume</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {isDrillDown ? (
          <ThemeTableSection
            queryId={selectedQuery.id}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            context={drillDownContext!}
            volumeType={volumeType}
            publicFilter={publicFilter}
          />
        ) : (
          <BrandProductTableSection
            queryId={selectedQuery.id}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            viewType={viewType}
            volumeType={volumeType}
            onDrillDown={drillDown}
            publicFilter={publicFilter}
          />
        )}
      </div>
    </div>
  );
}

// Separate components for each section
function BrandProductTableSection({
  queryId,
  startDate,
  endDate,
  viewType,
  volumeType,
  onDrillDown,
  publicFilter,
}: {
  queryId: string;
  startDate: string;
  endDate: string;
  viewType: 'brands' | 'products';
  volumeType: VolumeType;
  onDrillDown: (context: DrillDownContext) => void;
  publicFilter: PublicFilter;
}) {
  const brandQuery = useBrandsWithThemes({
    queryId,
    startDate,
    endDate,
    publicFilter,
  });

  const productQuery = useProductsWithThemes({
    queryId,
    startDate,
    endDate,
    publicFilter,
  });

  const currentQuery = viewType === 'brands' ? brandQuery : productQuery;

  return (
    <BrandProductTable
      data={currentQuery.data || []}
      type={viewType}
      volumeType={volumeType}
      onDrillDown={onDrillDown}
      isLoading={currentQuery.isFetching}
    />
  );
}

function ThemeTableSection({
  queryId,
  startDate,
  endDate,
  context,
  volumeType,
  publicFilter,
}: {
  queryId: string;
  startDate: string;
  endDate: string;
  context: DrillDownContext;
  volumeType: VolumeType;
  publicFilter: PublicFilter;
}) {
  const { data: themesData, isFetching } = useThemesByContext({
    queryId,
    startDate,
    endDate,
    context,
    publicFilter,
  });

  // Build parent filter based on context
  const parentFilter: Record<string, string> = {};
  if (context.type === 'brand') {
    parentFilter.brand = context.name;
  } else if (context.type === 'product') {
    parentFilter.product = context.name;
  }

  return (
    <ThemeTable
      data={themesData || []}
      volumeType={volumeType}
      sentiment={context.sentiment}
      parentDimension={context.type}
      parentFilter={parentFilter}
      isLoading={isFetching}
    />
  );
}
